import { useState } from 'react'
import type { AccountRole } from '@workflow/contracts'
import { Button, Field, TextField, cn } from '@workflow/ui'
import { useWhitelistMember } from '@/lib/api/hooks'
import { errorSentence } from '@/lib/api/client'
import { PosterDialog } from '@/shell/PosterDialog'
import { ROLE_LABEL } from './roles'

export function InviteDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const whitelist = useWhitelistMember()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AccountRole | ''>('')
  const close = () => {
    setEmail('')
    setRole('')
    whitelist.reset()
    onClose()
  }

  const roleChoice = (value: AccountRole, detail: string) => (
    <label className="relative block cursor-pointer">
      <input type="radio" name="invite-role" aria-label={ROLE_LABEL[value]} value={value} checked={role === value}
        onChange={() => setRole(value)} className="peer sr-only" disabled={whitelist.isPending} />
      <span className={cn('flex min-h-[72px] flex-col justify-center rounded-xl border px-3 py-3 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-disabled:opacity-50',
        role === value ? 'border-foreground/40 bg-selected' : 'border-hairline bg-surface-sunken hover:bg-tint')}>
        <span className="flex items-center justify-between gap-2 text-ui font-medium">
          {ROLE_LABEL[value]}
          <span aria-hidden className={cn('flex size-4 shrink-0 items-center justify-center rounded-full border', role === value ? 'border-foreground' : 'border-muted-foreground/40')}>
            {role === value ? <span className="size-2 rounded-full bg-foreground" /> : null}
          </span>
        </span>
        <span className="mt-1 text-caption text-muted-foreground">{detail}</span>
      </span>
    </label>
  )

  return (
    <PosterDialog
      open={open}
      onClose={close}
      labelledBy="invite-title"
      image="/art/onboarding-shoreline.png"
      colorArtwork
      alt="A blue shoreline painting with orange flowers"
      headline="Invite"
      caps="Workflow · Invitations"
    >
      <form
        className="flex flex-col px-6 py-6 sm:px-8 sm:py-7 lg:px-10"
        onSubmit={(event) => {
          event.preventDefault()
          if (!role || !email.trim() || whitelist.isPending) return
          whitelist.mutate(
            { email, role },
            {
              onSuccess: () => {
                close()
              },
            },
          )
        }}
      >
        <p className="text-micro text-muted-foreground uppercase">Invitation</p>
        <h3 id="invite-title" className="mt-1 font-display text-2xl text-foreground">
          Invite a new account
        </h3>
        <p className="mt-2 max-w-[42ch] text-caption text-muted-foreground">
          Add their email address. Nothing is sent — they can sign in immediately.
        </p>
        <Field label="Email" htmlFor="invite-email" className="mt-5">
          <TextField
            id="invite-email"
            value={email}
            placeholder="name@company.com"
            autoComplete="off"
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <fieldset className="mt-5" disabled={whitelist.isPending}>
          <legend className="text-ui font-medium">Choose an account role</legend>
          <p className="mt-1 text-caption text-muted-foreground">No role is selected by default.</p>
          <div className="mt-3 grid gap-4 min-[480px]:grid-cols-2">
            <div>
              <p className="mb-2 text-micro uppercase text-muted-foreground">Users</p>
              {roleChoice('player', 'Own tasks and payments')}
            </div>
            <div className="border-t border-hairline pt-4 min-[480px]:border-l min-[480px]:border-t-0 min-[480px]:pl-4 min-[480px]:pt-0">
              <p className="mb-2 text-micro uppercase text-muted-foreground">Studio access</p>
              <div className="flex flex-col gap-2">
                {roleChoice('staff', 'Manage studio work')}
                {roleChoice('admin', 'Full studio access')}
              </div>
            </div>
          </div>
          {role ? <p className="mt-3 text-caption text-muted-foreground" role="status">This account will have {ROLE_LABEL[role].toLowerCase()} access.</p> : null}
        </fieldset>
        <div className="mt-auto flex flex-wrap items-center gap-3 pt-6">
          <Button type="submit" variant="primary" busy={whitelist.isPending} disabled={!email.trim() || !role}>
            {role ? `Invite ${ROLE_LABEL[role].toLowerCase()}` : 'Choose a role to invite'}
          </Button>
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          {whitelist.error ? <p className="text-caption text-danger">{errorSentence(whitelist.error)}</p> : null}
        </div>
      </form>
    </PosterDialog>
  )
}
